import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PolicySettingsForm } from './PolicySettingsForm';
import { PolicyModeSelector } from './PolicyModeSelector';
import { AiInstructionCatalogDetails } from '@/components/AiInstructionCatalog/AiInstructionCatalogDetails';
import type {
  Policy,
  PolicyModeType,
} from '@kirin/api-client/contracts/policies';
import { PolicyKey, PolicyMode } from '@kirin/api-client/contracts/policies';
import { NEW_POLICY_KEYS, policyKeyHidesOuterModeControls } from '../constants';
import type { UsePolicySettingsHook } from '../types';
import {
  YOLO_MODES_DEPRECATION_MESSAGE,
  YOLO_MODES_OFF_CONFIRM_DESCRIPTION,
  YOLO_MODES_OFF_CONFIRM_TITLE,
} from '../policyListVisibility';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { useScopeChangeAction } from '@/hooks/useScopeChangeHooks';
import { FadeOutWrapper } from './FadeOutWrapper';

interface PolicyCardProps {
  policy: Policy;
  savingSettings: boolean;
  savingToggle: boolean;
  canEdit: boolean;
  usePolicySettings: UsePolicySettingsHook;
  onTogglePolicy: (enabled: boolean, mode?: PolicyModeType) => void;
  onSaveSettings: (
    policyKey: string,
    settings: Record<string, unknown>
  ) => void;
  onResetSettings: (policyKey: string) => void;
  fadeOut?: boolean;
  onFadeOutComplete?: () => void;
}

export function PolicyCard({
  policy,
  savingSettings,
  savingToggle,
  canEdit,
  usePolicySettings,
  onTogglePolicy,
  onSaveSettings,
  onResetSettings,
  fadeOut = false,
  onFadeOutComplete,
}: PolicyCardProps) {
  const [searchParams] = useSearchParams();
  // Deep-link auto-expand for the "Go to Policies" action on alerts (KIR-3558):
  // when the URL targets this policy via `?policy=<key>` the card starts open.
  // Derived at mount (the link opens a fresh tab) rather than via a setState-in-
  // effect, per react-hooks/set-state-in-effect.
  const [isExpanded, setIsExpanded] = useState(
    () => policy.hasSettings && searchParams.get('policy') === policy.key
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [yoloOffConfirmOpen, setYoloOffConfirmOpen] = useState(false);
  const { changeScopeTo } = useScopeChangeAction();
  const scope = `policy-setting-${policy.key}`;
  const cardHeaderRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = () => {
    if (isExpanded) {
      changeScopeTo({
        from: scope,
        id: `collapse-policy-settings-${policy.key}`,
        cancelLabel: 'Stay on Policy',
        discardLabel: 'Leave Policy',
        run: () => {
          setIsExpanded(prev => !prev);
        },
      });
    } else {
      setIsExpanded(true);
    }
  };

  const setPolicySettingsChanged = useCallback((changed: boolean) => {
    setHasUnsavedChanges(changed);
  }, []);

  const handleModeChange = useCallback(
    (mode: PolicyModeType) => {
      if (policy.key === PolicyKey.YoloModes && mode === PolicyMode.Off) {
        setYoloOffConfirmOpen(true);
        return;
      }
      onTogglePolicy(mode !== PolicyMode.Off, mode);
    },
    [policy.key, onTogglePolicy]
  );

  const confirmYoloOff = useCallback(() => {
    setYoloOffConfirmOpen(false);
    onTogglePolicy(false, PolicyMode.Off);
  }, [onTogglePolicy]);

  // KIR-3515 / KIR-3816: the AI Agent Instructions policy has no settings form
  // but is still expandable — its body shows the catalog chip + Manage-catalog
  // link. (The Secure AI Coding Enforcement policy lives on its own page and is
  // not shown on the Policies list.)
  const isCatalogPolicy = policy.key === PolicyKey.AiAgentInstructions;
  const canExpand = policy.hasSettings || isCatalogPolicy;

  // Deep-link scroll for the "Go to Policies" action on alerts (KIR-3558): the
  // card already starts expanded when the URL targets it (see the useState
  // initializer above); here we only scroll it into view so the Allow/Block
  // editor is immediately visible. This effect performs a DOM side effect only —
  // no setState — to satisfy react-hooks/set-state-in-effect.
  useEffect(() => {
    if (!canExpand) return;
    if (searchParams.get('policy') !== policy.key) return;
    cardHeaderRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [searchParams, policy.key, canExpand]);

  const isMultiLinePolicyDescription =
    policy.key === PolicyKey.McpServerSources ||
    policy.key === PolicyKey.ExtensionsManagement;
  const hideOuterModeControls = policyKeyHidesOuterModeControls(policy.key);
  const modeSelectorDisabled = savingToggle || !canEdit || fadeOut;

  return (
    <>
      <FadeOutWrapper
        className="space-y-0"
        active={fadeOut}
        onComplete={onFadeOutComplete}
      >
        <div
          ref={cardHeaderRef}
          className={`flex items-center gap-4 px-4 py-3 ${canExpand ? 'cursor-pointer hover:bg-muted/50' : ''}`}
          onClick={() => canExpand && toggleExpanded()}
        >
          <div className="flex items-center gap-2 min-w-0">
            {canExpand ? (
              isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )
            ) : (
              <div className="w-4" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            {hasUnsavedChanges && (
              <div className="w-fit min-w-0 mb-2 transition-mb text-yellow-500 text-xs p-1 px-2 bg-yellow-100 dark:bg-yellow-900/50 rounded-xs">
                You have unsaved changes
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-medium truncate">{policy.name}</div>
              {!hideOuterModeControls && (
                <Badge
                  variant={
                    (policy.mode ?? PolicyMode.Off) === PolicyMode.Enforce
                      ? 'secondary'
                      : 'outline'
                  }
                >
                  {(policy.mode ?? PolicyMode.Off) === PolicyMode.Off
                    ? 'Off'
                    : (policy.mode ?? PolicyMode.Off) === PolicyMode.Monitor
                      ? 'Monitor'
                      : 'Enforce'}
                </Badge>
              )}
              {NEW_POLICY_KEYS.has(policy.key) && (
                <Badge className="bg-blue-500 text-white">New</Badge>
              )}
            </div>
            <div
              className={`text-sm text-muted-foreground ${
                isMultiLinePolicyDescription
                  ? `whitespace-pre-line ${isExpanded ? '' : 'line-clamp-2'}`
                  : `${isExpanded ? '' : 'truncate'}`
              }`}
              title={
                isMultiLinePolicyDescription
                  ? policy.description.replace(/\n/g, ' ')
                  : policy.description
              }
              data-policy-desc={policy.key}
            >
              {policy.description}
            </div>
            {policy.key === PolicyKey.YoloModes && (
              <HighlightBox
                variant="warning"
                padding="sm"
                radius="md"
                className="mt-2 max-w-prose text-xs leading-snug text-amber-950 dark:text-amber-100"
                role="note"
              >
                {YOLO_MODES_DEPRECATION_MESSAGE}
              </HighlightBox>
            )}
          </div>
          {!hideOuterModeControls && (
            <div onClick={e => e.stopPropagation()} className="min-w-[140px]">
              <PolicyModeSelector
                value={(policy.mode ?? PolicyMode.Off) as PolicyModeType}
                onChange={handleModeChange}
                disabled={modeSelectorDisabled}
                supportsEnforce={policy.supportsEnforce ?? false}
                enforceDisabledReason={policy.enforceDisabledReason}
                enforceTooltip={policy.enforceTooltip}
              />
            </div>
          )}
        </div>
        {isExpanded && (policy.hasSettings || isCatalogPolicy) && (
          <div
            className={cn(
              'px-4 pb-4 pt-0 border-t bg-muted/20',
              // KIR-5640: the catalog body starts under the row title, not
              // under the chevron: px-4 (16) + chevron (16) + gap-4 (16).
              isCatalogPolicy && 'pl-12'
            )}
            data-testid="policy-card-expanded"
          >
            {isCatalogPolicy ? (
              <AiInstructionCatalogDetails />
            ) : (
              <PolicySettingsForm
                scope={scope}
                policyKey={policy.key}
                // KIR-4858: nested mode-valued sub-policies disable themselves
                // while the owning policy is Off. The settings endpoint does
                // not carry the policy mode, and this card is the only place
                // that holds the `Policy` object.
                parentMode={(policy.mode ?? PolicyMode.Off) as PolicyModeType}
                usePolicySettings={usePolicySettings}
                saving={savingSettings}
                onSave={onSaveSettings}
                onReset={onResetSettings}
                readOnly={!canEdit}
                onPolicySettingsDirty={setPolicySettingsChanged}
              />
            )}
          </div>
        )}
      </FadeOutWrapper>

      {policy.key === PolicyKey.YoloModes && (
        <AlertDialog
          open={yoloOffConfirmOpen}
          onOpenChange={setYoloOffConfirmOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {YOLO_MODES_OFF_CONFIRM_TITLE}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {YOLO_MODES_OFF_CONFIRM_DESCRIPTION}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmYoloOff}>
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
